import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  loadPortal,
  saveStammdaten,
  type DatenschutzSelections,
} from '@/core/data/portalViews'
import { signAnwesenheitsliste } from '@/variants/azav/lib/signAnwesenheitsliste'
import {
  clearPortalContext,
  isZweck,
  loadPortalContext,
  parseAccessToken,
  savePortalContext,
  stepForZweck,
  type PortalContext,
} from '@/core/data/participantAuth'
import { fetchMe, redeemLinkToken, signOut as apiSignOut, type Me as AuthSession } from '@/core/api/auth'
import { ApiError, TransportError } from '@/core/api/http'
import { resetMe, resolveParticipant } from '@/core/data/session'
import {
  participantCompleteOnGate,
  fetchDatenschutzConsentOnGate,
  fetchDatenschutzInfoOnGate,
  signDatenschutzOnGate,
  signVertragOnGate,
} from '@/core/api/portal'
import { generateDocument } from '@/core/api/documents'
import { downloadFile } from '@/core/data/storage'
import { downloadBlob } from '@/core/lib/files'
import { portalTabs, stammdatenForm } from '@/core/templates'
import { initialFormValues, updateFormField, type FormValues } from '@/core/components/form/formModel'
import { findMalformedPlz } from '@/core/lib/validate'
import type { FieldError, FieldErrors } from '@/core/lib/fieldError'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import {
  ButtonLink,
  Button,
  TopBar,
  Confirmation,
  Icon,
  LoadingState,
  Tabs,
  type TabsItem,
  Toaster,
  toastError,
} from 'admination-react'
import { StammdatenTab } from './StammdatenTab'
import { DatenschutzTab } from './DatenschutzTab'
import { VertragTab } from '@/variants/azav/components/teilnehmer/VertragTab'
import { NachweiseTab } from '@/variants/azav/components/teilnehmer/NachweiseTab'
import { AnwesenheitslisteTab } from '@/variants/azav/components/teilnehmer/AnwesenheitslisteTab'
import { ZertifikatTab } from '@/variants/azav/components/teilnehmer/ZertifikatTab'
import { SignaturePad } from '@/core/components/ui/signature-pad'
import { ListActionsProvider, type ListSelection } from '@/core/components/list/listActions'

type TabId = 'stammdaten' | 'datenschutz' | 'vertrag' | 'nachweise' | 'anwesenheitsliste' | 'zertifikat'

const EMPTY_DATENSCHUTZ_SELECTIONS: DatenschutzSelections = {
  art9: false,
  kontaktNachMassnahme: false,
  bewerbungsunterlagen: false,
}

type SigningTarget = 'vertrag' | 'datenschutz' | 'anwesenheitsliste' | null

const STAMMDATEN_FIELDS = new Set<string>(
  Object.values(stammdatenForm.sections).flatMap(({ fields }) => fields.map((field) => field.id)),
)

function validateStammdaten(values: FormValues): FieldErrors {
  const errors = new Map<string, FieldError>()
  for (const path of STAMMDATEN_FIELDS) {
    const [section, field] = path.split('.')
    const v = values[section]?.[field]
    if (typeof v !== 'string' || v.trim() === '') errors.set(path, { kind: 'missing' })
  }
  for (const path of findMalformedPlz(values, ['Kontakt.PLZ'])) {
    if (!errors.has(path)) errors.set(path, { kind: 'malformed', expected: 'plz' })
  }
  return errors
}

function ParticipantShell({
  step,
  coachingId,
  onDone,
}: {
  step: TabId
  coachingId: string

  onDone: (done: PortalDone) => void
}) {
  const [activeTab, setActiveTab] = useState<TabId>(step)
  const [data, setData] = useState<Awaited<ReturnType<typeof loadPortal>> | null>(null)
  const { portal = null, nachweise = [], datenschutz = null, anwesenheitsliste = null } = data ?? {}
  const [loadError, setLoadError] = useState<string | null>(null)
  const completed = (id: string) => id === 'stammdaten' ? !!portal?.tabs.stammdatenDone
    : (id === 'datenschutz' || id === 'vertrag') && !!portal?.tabs.vertragSigned
  const available = (id: string) => {
    if (completed(id)) return false
    if (id === 'stammdaten') return true
    if (id === 'datenschutz' || id === 'vertrag') return !!portal?.tabs.vertragReady
    if (id === 'nachweise') return nachweise.length > 0
    if (id === 'anwesenheitsliste') return !!portal?.tabs.anwesenheitslisteReady
    return !!portal?.tabs.zertifikatReady && !!anwesenheitsliste?.signed
  }
  const visibleTabs = portalTabs.filter((tab) => !completed(tab.id) && (tab.id === activeTab || available(tab.id)))
  const arrivalCorrected = useRef(false)
  const nextTab = portalTabs.find((tab) => available(tab.id))?.id as TabId | undefined
  const redirect = !!portal && ((!arrivalCorrected.current && !available(activeTab)) || completed(activeTab))
  useEffect(() => {
    if (!portal) return
    arrivalCorrected.current = true
    if (redirect && nextTab) setActiveTab(nextTab)
  }, [portal, activeTab, redirect, nextTab])

  const [stammdatenValues, setStammdatenValues] = useState<FormValues | null>(null)
  const [showStammdatenErrors, setShowStammdatenErrors] = useState(false)

  const [datenschutzSelections, setDatenschutzSelections] = useState<DatenschutzSelections>(
    EMPTY_DATENSCHUTZ_SELECTIONS,
  )

  const [signing, setSigning] = useState<SigningTarget>(null)

  const [barActions, setBarActions] = useState<ListSelection | null>(null)
  const [savingStammdaten, setSavingStammdaten] = useState(false)
  const savingStammdatenRef = useRef(false)

  const refresh = useCallback(async () => {
    const next = await loadPortal(coachingId)
    setData(next)
    return next
  }, [coachingId])

  const fetchInfo = useCallback(async () => {
    const response = await fetchDatenschutzInfoOnGate(coachingId)
    if (response.status === 404) return null
    if (!response.ok) {
      const body = await response.json().catch(() => null) as { detail?: unknown; error?: unknown } | null
      throw new ApiError(response.status, '', body?.detail ?? body?.error)
    }
    return response.blob()
  }, [coachingId])
  const fetchConsent = useCallback(() => fetchDatenschutzConsentOnGate(coachingId), [coachingId])

  useEffect(() => {
    if (datenschutz?.selections) setDatenschutzSelections(datenschutz.selections)
  }, [datenschutz])

  const stepRef = useRef(step)
  stepRef.current = step
  useEffect(() => {
    const step = stepRef.current
    let cancelled = false
    async function load() {
      const next = await loadPortal(coachingId)
      if (cancelled) return
      setData(next)
      setStammdatenValues(initialFormValues(stammdatenForm, next.stammdaten))
      if (step === 'vertrag') setActiveTab(next.datenschutz.signed ? 'vertrag' : 'datenschutz')
    }
    void load().catch((e: unknown) => {
      if (cancelled) return

      setLoadError(friendlyErrorMessage(e, "Portal konnte nicht geladen werden."))
    })
    return () => {
      cancelled = true
    }
  }, [coachingId])

  const markNachweisSigned = useCallback((dokumentId: string) => {
    setData((current) => current && ({ ...current,
      nachweise: current.nachweise.map((row) => row.id === dokumentId ? { ...row, signed: true } : row),
    }))
  }, [])

  const setStammdatenField = useCallback((path: string, v: unknown) => {
    setStammdatenValues((prev) => updateFormField(prev, path, v))
  }, [])

  const stammdatenErrors = useMemo(
    () => (stammdatenValues ? validateStammdaten(stammdatenValues) : undefined),
    [stammdatenValues],
  )

  async function handleSave() {
    if (!stammdatenValues || savingStammdatenRef.current) return
    if (stammdatenErrors && stammdatenErrors.size > 0) {
      setShowStammdatenErrors(true)
      return
    }
    setShowStammdatenErrors(false)
    savingStammdatenRef.current = true
    setSavingStammdaten(true)
    try {
      try {
        await saveStammdaten(coachingId, stammdatenValues)
      } catch (e) {

        toastError(friendlyErrorMessage(e, "Stammdaten konnten nicht gespeichert werden."))
        return
      }

      if (step === 'stammdaten') {
        try {
          await participantCompleteOnGate(coachingId)
        } catch (e) {

          toastError(friendlyErrorMessage(e, "Schritt konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut."))
          return
        }

        await apiSignOut().catch(() => {})
        clearPortalContext()
        onDone({ kind: 'stammdaten' })
        return
      }

      await refresh()
    } finally {
      savingStammdatenRef.current = false
      setSavingStammdaten(false)
    }
  }

  async function handleDownloadDatenschutz() {
    const path = datenschutz?.pdfPath
    if (!path) return
    const blob = await downloadFile(path)
    if (!blob) {
      toastError("Datei konnte nicht geladen werden.")
      return
    }
    downloadBlob(blob, "datenschutz.pdf")
  }

  function handleDatenschutzSelectionChange(key: keyof DatenschutzSelections, value: boolean) {
    setDatenschutzSelections((prev) => ({ ...prev, [key]: value }))
  }

  async function handleGenerateDatenschutz(selections: DatenschutzSelections) {
    try {
      await generateDocument('datenschutz', {
        coaching_id: coachingId,
        selections: {
          art9: selections.art9,
          kontakt_nach_massnahme: selections.kontaktNachMassnahme,
          bewerbungsunterlagen: selections.bewerbungsunterlagen,
        },
      })
      await refresh()
    } catch (e) {
      toastError(
        friendlyErrorMessage(e, "Einwilligung konnte nicht erstellt werden. Bitte versuchen Sie es erneut."),
      )
      throw e
    }
  }

  async function handleSignatureSubmit(png: string) {
    if (!signing) return
    const refreshError = "Unterschrift konnte nicht gespeichert werden."
    let failure: string = refreshError
    try {
      if (signing === 'vertrag') {
        failure = "Vertrag konnte nicht signiert werden. Bitte versuchen Sie es erneut."
        const { download_url: downloadUrl, datenschutz_download_url: dsUrl } = await signVertragOnGate(coachingId, png)
        failure = refreshError

        if (step === 'vertrag') {
          await apiSignOut().catch(() => {})
          clearPortalContext()
          onDone({ kind: 'vertrag', downloadUrl, datenschutzDownloadUrl: dsUrl })
          return
        }
      } else if (signing === 'anwesenheitsliste') {
        await signAnwesenheitsliste(coachingId, png)

        const next = await refresh()
        if (next.portal.tabs.zertifikatReady && next.anwesenheitsliste.signed) {
          setActiveTab('zertifikat')
        }
        return
      } else {
        failure = "Einwilligung konnte nicht signiert werden. Bitte versuchen Sie es erneut."
        await signDatenschutzOnGate(coachingId, png)
        failure = refreshError

        setActiveTab('vertrag')
      }
      await refresh()
    } catch (e) {

      toastError(friendlyErrorMessage(e, failure))
      throw e
    }
  }

  if (!data || !stammdatenValues) {
    return (
      <div className="page">
        <div className="layout layout--center" id="base-layout">
          {loadError ? (
            <div className="notice">
              <div className="notice-title">Zugang nicht möglich</div>
              <div>{loadError}</div>
            </div>
          ) : (
            <LoadingState>Ihr Portal wird geladen …</LoadingState>
          )}
        </div>
      </div>
    )
  }

  return (
    <ListActionsProvider value={setBarActions}>

      <div className="page" id="page">
        <div className="layout" id="base-layout">
          <TopBar
            className="bar--responsive"
            left={{ type: 'title', label: `${data.portal.session.nachname}, ${data.portal.session.vorname}`, icon: 'i-person', active: false }}
            center={<Tabs
              items={visibleTabs.map((t): TabsItem => ({ name: t.id, label: t.label, icon: t.icon }))}
              activeName={activeTab}
              onChange={(id) => setActiveTab(id as TabId)}
            />}
            right={
              <div className="pill-cluster">
                {barActions?.actions.map((a) => (
                  <div className="pill" key={a.id}>
                    <Button
                      type="button"
                      variant={a.primary ? 'primary' : 'default'}
                      iconOnly={a.iconOnly}
                      disabled={a.disabled}
                      loading={a.loading}
                      onClick={() => barActions.onRun(a.id)}
                      title={a.iconOnly ? a.label : undefined}
                    >
                      {a.icon && <Icon name={a.icon} />}
                      {!a.iconOnly && a.label}
                    </Button>
                  </div>
                ))}
              </div>
            }
          />

          {completed(activeTab) ?

            <main className="form" data-scroll-host>
              <div className="form-sheet">
                <section className="section">
                  <div className="section-body">

                    <Confirmation tone="success" title="Alles erledigt." inline>
                      Für diesen Schritt gibt es hier nichts mehr zu tun.</Confirmation>
                  </div>
                </section>
              </div>
            </main>
          : <>
              {activeTab === 'stammdaten' && (
                <StammdatenTab
                  values={stammdatenValues}
                  onChange={setStammdatenField}
                  errors={showStammdatenErrors ? stammdatenErrors : undefined}
                  manualFields={STAMMDATEN_FIELDS}
                  onSave={() => void handleSave()}
                  saving={savingStammdaten}

                />
              )}
              {activeTab === 'datenschutz' && (
                <DatenschutzTab
                  header={data.datenschutz.header}
                  selections={datenschutzSelections}
                  onChange={handleDatenschutzSelectionChange}
                  signed={data.datenschutz.signed}
                  pdfPath={data.datenschutz.pdfPath}
                  onFetchInfo={fetchInfo}
                  onFetchConsent={fetchConsent}
                  onGenerate={handleGenerateDatenschutz}
                  onSign={() => setSigning('datenschutz')}
                  onDownloadSigned={() => void handleDownloadDatenschutz()}
                />
              )}
              {activeTab === 'vertrag' && (
                <VertragTab vertrag={data.vertrag} onSign={() => setSigning('vertrag')} />
              )}
              {activeTab === 'nachweise' && (
                <NachweiseTab nachweise={data.nachweise} onSigned={markNachweisSigned} />
              )}
              {activeTab === 'anwesenheitsliste' && (
                <AnwesenheitslisteTab
                  anwesenheitsliste={data.anwesenheitsliste}
                  onSign={() => setSigning('anwesenheitsliste')}
                />
              )}
              {activeTab === 'zertifikat' && <ZertifikatTab zertifikat={data.zertifikat} />}
            </>
          }
        </div>

        <SignaturePad
          open={signing !== null}
          title={
            signing === 'datenschutz' ? "Datenschutz"
            : signing === 'anwesenheitsliste' ? "Teilnehmendenliste"
            : "Vertrag"
          }
          onSubmit={handleSignatureSubmit}
          onClose={() => setSigning(null)}
        />
      </div>
    </ListActionsProvider>
  )
}

export type PortalDone =
  | { kind: 'stammdaten' }
  | { kind: 'vertrag'; downloadUrl: string; datenschutzDownloadUrl: string | null }

function PortalDoneCard({ done }: { done: PortalDone }) {
  return <AuthCard confirmation>
    <Confirmation tone="success" title="Vielen Dank!" inline>
      {done.kind === 'stammdaten' ?
        "Ihre Daten wurden gespeichert. Dieser Zugang ist jetzt geschlossen - Sie können dieses Fenster schließen."
      : "Der Vertrag ist unterschrieben. Dieser Zugang ist jetzt geschlossen. Bitte laden Sie die Dokumente jetzt herunter. Die Download-Links sind nur kurz gültig."
      }
    </Confirmation>
    {done.kind === 'vertrag' && (
      <div className="confirmation-actions">
        {[
          { href: done.downloadUrl, label: "Vertrag herunterladen", variant: 'primary' as const },
          ...(done.datenschutzDownloadUrl ? [{ href: done.datenschutzDownloadUrl, label: "Datenschutz herunterladen", variant: 'default' as const }] : []),
        ].map(({ href, label, variant }) => <ButtonLink key={label} href={href} download target="_blank" rel="noopener noreferrer" variant={variant}>
          <Icon name="i-download" />{label}
        </ButtonLink>)}
      </div>
    )}
  </AuthCard>
}

function AuthCard({ children, confirmation = false }: { children: ReactNode; confirmation?: boolean }) {
  return (
    <div className="page">
      <div className="layout layout--center" id="base-layout">
        <div className={confirmation ? 'confirmation-panel' : 'notice'}>{children}</div>
      </div>
    </div>
  )
}

function LinkRefusedCard({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <AuthCard confirmation>
    <Confirmation tone="error" title="Zugang nicht möglich" inline>
      {message}
    </Confirmation>
    {onRetry && (
      <div className="confirmation-actions">
        <Button variant="primary" onClick={onRetry}>
          Erneut versuchen</Button>
      </div>
    )}
  </AuthCard>
}

async function resolvePortalContext(
  session: Pick<AuthSession, 'user_id' | 'purpose' | 'participant_step' | 'expires_at'> | null,
): Promise<PortalContext> {
  if (!session || session.purpose !== 'teilnahme' || !isZweck(session.participant_step)) {
    throw new Error(GENERIC_RESUME_ERROR)
  }
  const who = await resolveParticipant(session, null)
  if (who.coachingIds.length !== 1) throw new Error(GENERIC_RESUME_ERROR)
  return {
    zweck: session.participant_step,
    coachingId: who.coachingIds[0],
    expiresAt: session.expires_at ?? null,
  }
}

const GENERIC_ACCESS_ERROR = "Zugang nicht möglich - bitte später erneut versuchen."
const GENERIC_RESUME_ERROR =
  "Dieser Link ist ungültig, abgelaufen oder bereits abgeschlossen. Bitte fordern Sie einen neuen Link an."
const LINK_EXPIRED_MESSAGE = "Der Link ist abgelaufen - bitte einen neuen Link anfordern."
const IDENTITY_COLLISION_MESSAGE =
  "Ihre E-Mail-Adresse gehört bereits zu einem anderen Konto. Bitte wenden Sie sich an Ihren Coach."

type LinkRefusal = { message: string; retryable: boolean }

function linkRefusal(error: unknown): LinkRefusal {
  if (error instanceof ApiError && error.path === '/auth/link' && error.status === 409) {
    return { message: friendlyErrorMessage(error, IDENTITY_COLLISION_MESSAGE), retryable: false }
  }
  if (error instanceof ApiError && error.path === '/auth/link' && (error.status === 404 || error.status === 410)) {
    return { message: friendlyErrorMessage(error, GENERIC_RESUME_ERROR), retryable: false }
  }
  const retryable = error instanceof TransportError ||
    (error instanceof ApiError && (error.status === 429 || (error.status >= 500 && error.status < 600)))
  return {
    message: retryable ? GENERIC_ACCESS_ERROR : GENERIC_RESUME_ERROR,
    retryable,
  }
}

const IDLE_LOGOUT_MESSAGE =
  "Aus Sicherheitsgründen automatisch abgemeldet - bitte den Link erneut öffnen."
const IDLE_TIMEOUT_MS = 30 * 60 * 1000

const MAX_TIMEOUT_MS = 2 ** 31 - 1

type GateStatus = 'loading' | 'refused' | 'error' | 'ready' | { done: PortalDone }

function ParticipantAuthGate() {
  const [token] = useState(() => parseAccessToken(window.location.pathname, window.location.search))
  const [status, setStatus] = useState<GateStatus>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<LinkRefusal | null>(null)
  const [context, setContext] = useState<PortalContext | null>(null)

  useEffect(() => {
    if (token) return
    let cancelled = false
    async function tryResume() {
      if (!loadPortalContext()) {
        if (!cancelled) setStatus('error')
        return
      }
      resetMe()
      const nextContext = await fetchMe().then(resolvePortalContext).catch(() => null)
      if (cancelled) return
      if (!nextContext) {
        setStatus('error')
        return
      }
      savePortalContext(nextContext)
      setContext(nextContext)
      setStatus('ready')
    }
    void tryResume()
    return () => {
      cancelled = true
    }
  }, [token])

  const continueRef = useRef(handleContinue)
  continueRef.current = handleContinue
  useEffect(() => {
    if (!token) return
    void continueRef.current()
  }, [token])

  async function handleContinue() {
    if (!token) return
    setStatus('loading')
    clearPortalContext()
    resetMe()
    let nextContext: PortalContext
    try {
      const result = await redeemLinkToken(token)
      nextContext = await resolvePortalContext(result)
    } catch (error) {
      setRefusal(linkRefusal(error))
      setStatus('refused')
      return
    }

    savePortalContext(nextContext)

    window.history.replaceState(null, '', '/t')
    setContext(nextContext)
    setStatus('ready')
  }

  async function forceLogout(message: string) {
    await apiSignOut().catch(() => {})
    clearPortalContext()
    setErrorMessage(message)
    setStatus('error')
  }

  useEffect(() => {
    if (status !== 'ready' || !context?.expiresAt) return
    const expiresAtMs = new Date(context.expiresAt).getTime()
    if (Number.isNaN(expiresAtMs)) return
    let timer: number
    function arm() {
      const delay = Math.min(Math.max(expiresAtMs - Date.now(), 0), MAX_TIMEOUT_MS)
      timer = window.setTimeout(() => {
        if (Date.now() >= expiresAtMs) void forceLogout(LINK_EXPIRED_MESSAGE)
        else arm()
      }, delay)
    }
    arm()
    return () => window.clearTimeout(timer)
  }, [status, context?.expiresAt])

  useEffect(() => {
    if (status !== 'ready') return
    let timer: number
    function resetIdleTimer() {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => void forceLogout(IDLE_LOGOUT_MESSAGE), IDLE_TIMEOUT_MS)
    }
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const
    resetIdleTimer()
    for (const evt of events) window.addEventListener(evt, resetIdleTimer, { passive: true })
    return () => {
      window.clearTimeout(timer)
      for (const evt of events) window.removeEventListener(evt, resetIdleTimer)
    }
  }, [status])

  if (typeof status === 'object') {
    return <PortalDoneCard done={status.done} />
  }

  if (status === 'loading') {
    return <AuthCard><LoadingState>Ihr Zugang wird geprüft …</LoadingState></AuthCard>
  }

  if (status === 'refused') {
    return (
      <LinkRefusedCard
        message={refusal?.message ?? GENERIC_ACCESS_ERROR}
        onRetry={refusal?.retryable ? () => void handleContinue() : undefined}
      />
    )
  }

  if (status === 'error') {
    return (
      <AuthCard>
        <div className="notice-title">Zugang nicht möglich</div>
        <div>{errorMessage ?? GENERIC_RESUME_ERROR}</div>
      </AuthCard>
    )
  }

  const step = stepForZweck(context!.zweck)
  return (
      <ParticipantShell
        step={step}
        coachingId={context!.coachingId}
        onDone={(done) => setStatus({ done })}
      />
  )
}

export function ParticipantApp() {
  return (
    <Toaster>
      <ParticipantAuthGate />
    </Toaster>
  )
}
